import { useMemo } from 'react';
import type { DriveSafe } from '@vivport/backend/types';
import { Folder, Loader2 } from 'lucide-react';
import { DriveFileRow } from '@/components/drives/DriveFileRow';
import { resolveKeptRef } from '@/components/keep/kept-ref';
import { useUser } from '@/hooks';
import { DRIVE_LIVE_FIND_LIMIT, useDriveLiveFind, useDriveMirroredRefs, useDrives } from '@/hooks/useDrivesQueries';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { hasSearchableText, useLiveSearchGate } from '@/utils/explore-live-search';

export interface ExploreDriveResultsProps {
  drives: boolean;
  searchText: string;
}

interface ExploreDriveBlockProps {
  drive: DriveSafe;
  searchText: string;
}

function ExploreDriveBlock({ drive, searchText }: ExploreDriveBlockProps) {
  const gate = useLiveSearchGate(searchText, hasSearchableText);
  const findQuery = useDriveLiveFind(drive.id, gate.params, gate.ready);
  const entries = useMemo(() => findQuery.data?.entries ?? [], [findQuery.data?.entries]);
  const folders = useMemo(() => entries.filter((entry) => entry.isFolder), [entries]);
  const files = useMemo(() => entries.filter((entry) => !entry.isFolder), [entries]);
  const filePaths = useMemo(() => files.map((entry) => entry.path), [files]);
  const refsQuery = useDriveMirroredRefs(drive.id, filePaths);
  const count =
    findQuery.data !== undefined
      ? ` · ${entries.length} shown${entries.length === DRIVE_LIVE_FIND_LIMIT ? ' · limit reached' : ''}`
      : '';
  const heading = `${drive.productLabel} · ${drive.name}${count}`;

  return (
    <div className="mt-3">
      <p className="min-w-0 truncate text-xs text-muted-foreground">{heading}</p>
      {!gate.ready || findQuery.isPending ? (
        <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Searching {drive.name}…
        </p>
      ) : findQuery.isError ? (
        <p className="mt-1 text-xs text-destructive">
          {getAxiosErrorDisplayMessage(findQuery.error, 'Drive search failed')}
        </p>
      ) : entries.length === 0 ? (
        <p className="mt-1 text-muted-foreground">Nothing on {drive.name} matched</p>
      ) : (
        <>
          {folders.length > 0 ? (
            <ul className="mt-1 divide-y divide-border">
              {folders.map((folder) => (
                <li key={folder.path} className="flex items-center gap-2 py-2">
                  <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 truncate">{folder.name}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {files.length > 0 ? (
            <ul className="mt-1 divide-y divide-border">
              {files.map((entry) => (
                <DriveFileRow
                  key={entry.path}
                  file={{
                    name: entry.name,
                    path: entry.path,
                    sizeBytes: entry.sizeBytes,
                    mimeType: entry.mimeType,
                    modifiedAt: entry.modifiedAt,
                    webUrl: entry.webUrl,
                    mirroredContentId: entry.mirroredContentId,
                    mirroredCode: entry.mirroredCode,
                  }}
                  driveId={drive.id}
                  productLabel={drive.productLabel}
                  kept={resolveKeptRef(refsQuery.data, entry.path, entry.mirroredContentId, entry.mirroredCode)}
                />
              ))}
            </ul>
          ) : null}
        </>
      )}
    </div>
  );
}

export function ExploreDriveResults({ drives, searchText }: ExploreDriveResultsProps) {
  const { data: memberContext } = useUser();
  const memberId = memberContext?.selectedMember?.id ?? 0;
  const drivesQuery = useDrives(memberId);
  const driveList = drivesQuery.data?.drives ?? [];

  if (!drives || driveList.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 border-t border-border pt-4 text-sm">
      <h2 className="font-medium">On your drives</h2>
      {hasSearchableText(searchText) ? (
        driveList.map((drive) => <ExploreDriveBlock key={drive.id} drive={drive} searchText={searchText} />)
      ) : (
        <p className="mt-2 text-muted-foreground">Add words to search your drives</p>
      )}
    </div>
  );
}
